import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert } from 'react-native';

import AppTextInput from './AppTextInput';
interface Appointment {
  id: string;
  customerName: string;
  phone: string;
  email: string;
  address: string;
  serviceType: string;
  hairLength: string;
  hairType: string;
  previousColor: string;
  specialInstructions: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  workerId?: string;
  workerName?: string;
  actualDuration?: number;
}

interface InvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onSend: () => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  visible,
  onClose,
  appointment,
  onSend,
}) => {
  const [invoiceData, setInvoiceData] = useState({
    baseRate: 75,
    stylingRate: 25,
    colorRate: 50,
    additionalRate: 30,
    hourlyRate: 40,
    taxRate: 8.5,
    discount: 0,
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
  });

  useEffect(() => {
    if (appointment) {
      calculateTotals();
    }
  }, [appointment, invoiceData]);

  const calculateTotals = () => {
    if (!appointment) return;

    let subtotal = invoiceData.baseRate;
    
    // Add service-specific rates
    if (appointment.serviceType.includes('Styling')) {
      subtotal += invoiceData.stylingRate;
    }
    if (appointment.serviceType.includes('Color')) {
      subtotal += invoiceData.colorRate;
    }
    if (appointment.specialInstructions.includes('Additional')) {
      subtotal += invoiceData.additionalRate;
    }
    
    // Add hourly rate based on duration
    if (appointment.actualDuration) {
      const hours = appointment.actualDuration / 60;
      subtotal += hours * invoiceData.hourlyRate;
    }

    const tax = subtotal * (invoiceData.taxRate / 100);
    const discount = invoiceData.discount;
    const total = subtotal + tax - discount;

    setTotals({ subtotal, tax, discount, total });
  };

  const updateInvoiceData = (field: keyof typeof invoiceData, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInvoiceData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleSendInvoice = () => {
    Alert.alert(
      'Send Invoice',
      `Send invoice for $${totals.total.toFixed(2)} to ${appointment?.customerName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Send',
          onPress: () => {
            onSend();
            Alert.alert('Success', 'Invoice sent successfully!');
          },
        },
      ]
    );
  };

  if (!appointment) return null;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Send AutoCare Invoice</Text>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content}>
          {/* Customer Information */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Customer Information</Text>
            <View style={styles.customerInfo}>
              <Text style={styles.customerName}>{appointment.customerName}</Text>
              <Text style={styles.customerDetails}>📞 {appointment.phone}</Text>
              <Text style={styles.customerDetails}>📧 {appointment.email}</Text>
              <Text style={styles.customerDetails}>📍 {appointment.address}</Text>
            </View>
          </View>

          {/* Service Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Service Details</Text>
            <View style={styles.serviceInfo}>
              <Text style={styles.serviceItem}>
                💇‍♀️ Service: {appointment.serviceType}
              </Text>
              <Text style={styles.serviceItem}>
                🎨 Type: {appointment.specialInstructions}
              </Text>
              <Text style={styles.serviceItem}>
                📅 Date: {appointment.appointmentDate} at {appointment.appointmentTime}
              </Text>
              {appointment.workerName && (
                <Text style={styles.serviceItem}>
                  👤 Stylist: {appointment.workerName}
                </Text>
              )}
              {appointment.actualDuration && (
                <Text style={styles.serviceItem}>
                  ⏱️ Duration: {appointment.actualDuration} minutes
                </Text>
              )}
            </View>
          </View>

          {/* Invoice Breakdown */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Invoice Breakdown</Text>
            
            <View style={styles.invoiceItem}>
              <Text style={styles.invoiceLabel}>Base Hair Service:</Text>
              <Text style={styles.invoiceValue}>${invoiceData.baseRate.toFixed(2)}</Text>
            </View>

            {appointment.serviceType.includes('Styling') && (
              <View style={styles.invoiceItem}>
                <Text style={styles.invoiceLabel}>Styling & Blowout:</Text>
                <Text style={styles.invoiceValue}>${invoiceData.stylingRate.toFixed(2)}</Text>
              </View>
            )}

            {appointment.serviceType.includes('Color') && (
              <View style={styles.invoiceItem}>
                <Text style={styles.invoiceLabel}>Color Treatment:</Text>
                <Text style={styles.invoiceValue}>${invoiceData.colorRate.toFixed(2)}</Text>
              </View>
            )}

            {appointment.specialInstructions.includes('Additional') && (
              <View style={styles.invoiceItem}>
                <Text style={styles.invoiceLabel}>Additional Services:</Text>
                <Text style={styles.invoiceValue}>${invoiceData.additionalRate.toFixed(2)}</Text>
              </View>
            )}

            {appointment.actualDuration && (
              <View style={styles.invoiceItem}>
                <Text style={styles.invoiceLabel}>
                  Time ({(appointment.actualDuration / 60).toFixed(1)} hrs × ${invoiceData.hourlyRate}/hr):
                </Text>
                <Text style={styles.invoiceValue}>
                  ${((appointment.actualDuration / 60) * invoiceData.hourlyRate).toFixed(2)}
                </Text>
              </View>
            )}

            <View style={styles.invoiceItem}>
              <Text style={styles.invoiceLabel}>Subtotal:</Text>
              <Text style={styles.invoiceValue}>${totals.subtotal.toFixed(2)}</Text>
            </View>

            <View style={styles.invoiceItem}>
              <Text style={styles.invoiceLabel}>Tax ({invoiceData.taxRate}%):</Text>
              <Text style={styles.invoiceValue}>${totals.tax.toFixed(2)}</Text>
            </View>

            {invoiceData.discount > 0 && (
              <View style={styles.invoiceItem}>
                <Text style={styles.invoiceLabel}>Discount:</Text>
                <Text style={[styles.invoiceValue, styles.discountValue]}>
                  -${totals.discount.toFixed(2)}
                </Text>
              </View>
            )}

            <View style={[styles.invoiceItem, styles.totalItem]}>
              <Text style={styles.totalLabel}>Total:</Text>
              <Text style={styles.totalValue}>${totals.total.toFixed(2)}</Text>
            </View>
          </View>

          {/* Editable Rates */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Adjust Rates (Optional)</Text>
            
            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Base Rate:</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.baseRate.toString()}
                onChangeText={(value) => updateInvoiceData('baseRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Styling Rate:</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.stylingRate.toString()}
                onChangeText={(value) => updateInvoiceData('stylingRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Color Rate:</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.colorRate.toString()}
                onChangeText={(value) => updateInvoiceData('colorRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Additional Rate:</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.additionalRate.toString()}
                onChangeText={(value) => updateInvoiceData('additionalRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Hourly Rate:</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.hourlyRate.toString()}
                onChangeText={(value) => updateInvoiceData('hourlyRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Tax Rate (%):</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.taxRate.toString()}
                onChangeText={(value) => updateInvoiceData('taxRate', value)}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.rateInput}>
              <Text style={styles.rateLabel}>Discount ($):</Text>
              <AppTextInput
                style={styles.rateInputField}
                value={invoiceData.discount.toString()}
                onChangeText={(value) => updateInvoiceData('discount', value)}
                keyboardType="numeric"
              />
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity style={styles.sendButton} onPress={handleSendInvoice}>
              <Text style={styles.sendButtonText}>Send Invoice</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  closeButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
  },
  customerInfo: {
    marginBottom: 10,
  },
  customerName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  customerDetails: {
    fontSize: 14,
    color: '#666',
    marginBottom: 3,
  },
  serviceInfo: {
    marginBottom: 10,
  },
  serviceItem: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  invoiceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  invoiceLabel: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  invoiceValue: {
    fontSize: 14,
    color: '#333',
    fontWeight: '600',
  },
  discountValue: {
    color: '#dc3545',
  },
  totalItem: {
    borderTopWidth: 2,
    borderTopColor: '#007bff',
    marginTop: 10,
    paddingTop: 15,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007bff',
  },
  rateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  rateLabel: {
    fontSize: 14,
    color: '#333',
    width: 120,
  },
  rateInputField: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    flex: 1,
    textAlign: 'right',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: 20,
    marginTop: 0,
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
    marginRight: 10,
  },
  cancelButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  sendButton: {
    backgroundColor: '#28a745',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
    marginLeft: 10,
  },
  sendButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export default InvoiceModal;